import {
	LOCATIONS_SECTION,
	parsePageHash,
	setLocationsPageUrl,
	isLocationsSlideActive,
} from "./page-hash";

const LOCATIONS_NAV_REVEAL_DELAY_MS = 380;

let locationsNavRevealTimer = null;

function prefersReducedMotion() {
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function clearLocationsNavRevealTimer() {
	if (locationsNavRevealTimer) {
		window.clearTimeout(locationsNavRevealTimer);
		locationsNavRevealTimer = null;
	}
}

function resetLocationsNavReveal() {
	clearLocationsNavRevealTimer();
	document
		.querySelectorAll(".fullpage-slide__locations-nav")
		.forEach((nav) => {
			nav.classList.remove("is-revealed", "is-hint-dismissed");
		});
}

function dismissLocationsNavHint() {
	const nav = document.querySelector(
		".fullpage-slide--locations .fullpage-slide__locations-nav",
	);

	nav?.classList.add("is-hint-dismissed");
}

function revealLocationsNav() {
	const nav = document.querySelector(
		".fullpage-slide--locations .fullpage-slide__locations-nav",
	);

	if (!nav || nav.classList.contains("is-revealed")) {
		return;
	}

	clearLocationsNavRevealTimer();
	nav.classList.remove("is-revealed");
	// Force the off-screen start state before revealing.
	void nav.offsetWidth;

	const delay = prefersReducedMotion() ? 0 : LOCATIONS_NAV_REVEAL_DELAY_MS;

	locationsNavRevealTimer = window.setTimeout(() => {
		locationsNavRevealTimer = null;

		if (isLocationsSlideActive()) {
			nav.classList.add("is-revealed");
		}
	}, delay);
}

function bindLocationsNavReveal() {
	document.addEventListener("paaadel:fullpage-slide-change", (event) => {
		const section = event.detail?.section || "";
		const previousSection = event.detail?.previousSection || "";

		if (section !== LOCATIONS_SECTION) {
			resetLocationsNavReveal();
			return;
		}

		if (previousSection !== "" && previousSection !== LOCATIONS_SECTION) {
			revealLocationsNav();
		}
	});

	if (isLocationsSlideActive()) {
		revealLocationsNav();
	}
}

function getVisualPanels(root) {
	return Array.from(root.querySelectorAll("[data-locations-visual-panel]"));
}

function getNamePanels(root) {
	return Array.from(root.querySelectorAll("[data-locations-name-panel]"));
}

function getLocationIdForIndex(root, index) {
	const panels = getNamePanels(root);
	const panel = panels[index];

	return panel?.dataset.locationId || String(index);
}

function resolveLocationIndex(root, locationId) {
	if (!locationId) {
		return 0;
	}

	const panels = getNamePanels(root);
	const decoded = decodeURIComponent(locationId);
	const bySlug = panels.findIndex(
		(panel) => panel.dataset.locationId === decoded,
	);

	if (bySlug >= 0) {
		return bySlug;
	}

	const asIndex = Number.parseInt(decoded, 10);
	if (Number.isFinite(asIndex) && asIndex >= 0 && asIndex < panels.length) {
		return asIndex;
	}

	return 0;
}

function resolveLocationIndexFromUrl(root) {
	const { section, locationId } = parsePageHash();

	if (section !== LOCATIONS_SECTION || !locationId) {
		return null;
	}

	return resolveLocationIndex(root, locationId);
}

function setPanelState(panel, active) {
	panel.classList.toggle("is-active", active);
	panel.hidden = !active;
}

function syncLocationsUrl(root, { push = false, force = false } = {}) {
	if (!force && !isLocationsSlideActive()) {
		return;
	}

	const index = Number.parseInt(root.dataset.locationsIndex || "0", 10);
	const locationId = getLocationIdForIndex(root, index);

	setLocationsPageUrl(locationId, { push });
}

function setCarouselIndex(
	root,
	nextIndex,
	{ updateUrl = true, push = false, forceUrl = false } = {},
) {
	const visualPanels = getVisualPanels(root);
	const namePanels = getNamePanels(root);
	const count = visualPanels.length;

	if (count <= 0) {
		return;
	}

	const normalized = ((nextIndex % count) + count) % count;

	visualPanels.forEach((panel, index) => {
		setPanelState(panel, index === normalized);
	});
	namePanels.forEach((panel, index) => {
		setPanelState(panel, index === normalized);
	});

	root.dataset.locationsIndex = String(normalized);

	if (updateUrl) {
		syncLocationsUrl(root, {
			push,
			force: forceUrl || isLocationsSlideActive(),
		});
	}
}

function stepCarousel(root, direction) {
	const count = getVisualPanels(root).length;
	if (count <= 1) {
		return;
	}

	const current = Number.parseInt(root.dataset.locationsIndex || "0", 10);
	const delta = direction === "prev" ? -1 : 1;

	setCarouselIndex(root, current + delta, { push: true, forceUrl: true });
}

function bindCarousel(root) {
	if (root.dataset.locationsCarouselBound === "true") {
		return;
	}

	const count = getVisualPanels(root).length;
	if (count <= 1) {
		return;
	}

	root.dataset.locationsCarouselBound = "true";

	root.addEventListener("click", (event) => {
		const button = event.target.closest("[data-locations-direction]");
		if (!button || !root.contains(button)) {
			return;
		}

		event.preventDefault();
		dismissLocationsNavHint();
		stepCarousel(root, button.dataset.locationsDirection);
	});
}

function handleLocationsSlideEnter() {
	document.querySelectorAll("[data-locations-carousel]").forEach((root) => {
		const fromUrl = resolveLocationIndexFromUrl(root);

		if (fromUrl !== null) {
			setCarouselIndex(root, fromUrl, {
				updateUrl: true,
				forceUrl: true,
			});
			return;
		}

		const index = Number.parseInt(root.dataset.locationsIndex || "0", 10);
		setCarouselIndex(root, index, {
			updateUrl: true,
			forceUrl: true,
		});
	});
}

function applyLocationFromHash() {
	const { section, locationId } = parsePageHash();

	if (
		section !== LOCATIONS_SECTION ||
		!locationId ||
		!isLocationsSlideActive()
	) {
		return;
	}

	document.querySelectorAll("[data-locations-carousel]").forEach((root) => {
		const index = resolveLocationIndex(root, locationId);
		setCarouselIndex(root, index, { updateUrl: false });
	});
}

function bindLocationsUrlSync() {
	window.addEventListener("popstate", applyLocationFromHash);
	window.addEventListener("hashchange", applyLocationFromHash);
}

function bindLocationsSlideEnter() {
	document.addEventListener("paaadel:fullpage-slide-change", (event) => {
		const section = event.detail?.section || "";
		const previousSection = event.detail?.previousSection || "";

		if (
			section === LOCATIONS_SECTION &&
			previousSection !== "" &&
			previousSection !== LOCATIONS_SECTION
		) {
			handleLocationsSlideEnter();
		}
	});
}

function initLocationsRoot(root) {
	bindCarousel(root);

	const fromUrl = resolveLocationIndexFromUrl(root);
	const fallback = Number.parseInt(root.dataset.locationsIndex || "0", 10);
	const initialIndex = fromUrl !== null ? fromUrl : fallback;

	setCarouselIndex(root, initialIndex, {
		updateUrl: fromUrl !== null || isLocationsSlideActive(),
		forceUrl: fromUrl !== null,
	});
}

export function initLocationsCarousel() {
	document
		.querySelectorAll("[data-locations-carousel]")
		.forEach(initLocationsRoot);
	bindLocationsUrlSync();
	bindLocationsSlideEnter();
	bindLocationsNavReveal();
}
